import {
  CommandPalette,
  CommandPaletteTrigger,
  Icons,
  useCommandPalette,
  type CommandPaletteItem,
  type LucideIcon,
} from '@munifocus/ui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { globalSearch } from '../lib/search-api';

const GROUP_ORDER = ['commands', 'bulletin', 'trainingSession'];

const COMMANDS: { key: string; href: string; icon: LucideIcon }[] = [
  { key: 'home', href: '/', icon: Icons.House },
  { key: 'bulletin', href: '/bulletin', icon: Icons.ScrollText },
  { key: 'training', href: '/training', icon: Icons.GraduationCap },
  { key: 'services', href: '/services', icon: Icons.Workflow },
  { key: 'about', href: '/about', icon: Icons.Landmark },
  { key: 'portal', href: '/portal', icon: Icons.KeyRound },
];

const RESULT_ICONS: Record<string, LucideIcon> = {
  bulletin: Icons.ScrollText,
  trainingSession: Icons.GraduationCap,
};

/**
 * The public site's Cmd-K palette plus its header affordance.
 *
 * Rendered on the deep-teal site header, so the trigger runs in its `onDark`
 * variant. Search results come from the anonymous public endpoint — nothing
 * here is gated, and nothing unpublished can appear.
 */
export function GlobalSearch() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { open, openPalette, closePalette, shortcutLabel } = useCommandPalette();
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  const commands = useMemo<CommandPaletteItem[]>(
    () =>
      COMMANDS.map((command) => ({
        id: `command:${command.key}`,
        group: 'commands',
        title: t(`web.commandPalette.commands.${command.key}`),
        href: command.href,
        icon: command.icon,
        keywords: t(`web.commandPalette.keywords.${command.key}`),
      })),
    [t],
  );

  const search = useCallback(
    async (query: string, signal: AbortSignal): Promise<CommandPaletteItem[]> => {
      const results = await globalSearch(query, lang, signal);
      return results.map((result) => ({
        id: `${result.type}:${result.id}`,
        group: result.type,
        title: result.title,
        subtitle: result.subtitle,
        href: result.href,
        icon: RESULT_ICONS[result.type],
      }));
    },
    [lang],
  );

  const groupLabels = useMemo(
    () => ({
      commands: t('common.commandPalette.groups.commands'),
      bulletin: t('web.commandPalette.groups.bulletin'),
      trainingSession: t('web.commandPalette.groups.trainingSession'),
    }),
    [t],
  );

  const labels = useMemo(
    () => ({
      title: t('common.commandPalette.title'),
      placeholder: t('web.commandPalette.placeholder'),
      empty: t('common.commandPalette.empty'),
      loading: t('common.commandPalette.loading'),
      noResults: t('common.commandPalette.noResults'),
      resultCountLabel: (count: number) => t('common.commandPalette.resultCount', { count }),
      hintNavigate: t('common.commandPalette.hintNavigate'),
      hintSelect: t('common.commandPalette.hintSelect'),
      hintClose: t('common.commandPalette.hintClose'),
    }),
    [t],
  );

  const onSelect = useCallback(
    (item: CommandPaletteItem) => {
      if (item.href) navigate(item.href);
    },
    [navigate],
  );

  return (
    <>
      <CommandPaletteTrigger
        onClick={openPalette}
        label={t('common.commandPalette.trigger')}
        shortcutLabel={shortcutLabel}
        onDark
      />
      <CommandPalette
        open={open}
        onClose={closePalette}
        commands={commands}
        search={search}
        onSelect={onSelect}
        groupLabels={groupLabels}
        groupOrder={GROUP_ORDER}
        labels={labels}
      />
    </>
  );
}
